import { lazy, Suspense } from 'react'
import type { ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth/useAuth'
import { Spinner } from './components/Spinner'
import { AppShell } from './layout/AppShell'
import AuthPage from './pages/AuthPage'
import NotFound from './pages/NotFound'

// Route-level code splitting: the dashboard and the meeting (WebRTC SDK) load on demand.
const Overview = lazy(() => import('./pages/Overview'))
const Rooms = lazy(() => import('./pages/Rooms'))
const RoomDetail = lazy(() => import('./pages/RoomDetail'))
const Recordings = lazy(() => import('./pages/Recordings'))
const Developers = lazy(() => import('./pages/Developers'))
const Settings = lazy(() => import('./pages/Settings'))
const Team = lazy(() => import('./pages/Team'))
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'))
const JoinPage = lazy(() => import('./pages/JoinPage'))

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <Spinner fullscreen />
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  return children
}

export default function App() {
  return (
    <Suspense fallback={<Spinner fullscreen />}>
      <Routes>
        <Route path="/login" element={<AuthPage />} />
        {/* Sign-up is invite-only; old links land on sign-in. */}
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/r/:slug" element={<JoinPage />} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Overview />} />
          <Route path="rooms" element={<Rooms />} />
          <Route path="rooms/:id" element={<RoomDetail />} />
          <Route path="recordings" element={<Recordings />} />
          <Route path="developers" element={<Developers />} />
          <Route path="team" element={<Team />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}
